import React, { useState } from "react";
import { AlertTriangle, Lock, Receipt, TrendingUp, Package, FileDown, Hash, Landmark } from "lucide-react";
import { Button, Badge, Spinner } from "../lib/ui";
import { openDoc } from "../lib/api";
import { useToast } from "../lib/toast";

const rp = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");

const FinRow = ({ l, v, tone, strong }) => (
  <div className={`flex items-center justify-between py-0.5 ${strong ? "mt-0.5 border-t border-line/60 pt-1 font-bold text-ink" : ""}`}>
    <span className={strong ? "text-ink" : "text-ink-soft"}>{l}</span>
    <span className={`font-semibold tabular-nums ${tone === "danger" ? "text-danger" : tone === "success" ? "text-emerald-600" : "text-ink"}`}>{v}</span>
  </div>
);
const Tot = ({ l, v, tone, strong, testid }) => (
  <div className="bg-surface px-4 py-3" data-testid={testid}>
    <p className="text-[11px] text-ink-mute">{l}</p>
    <p className={`font-heading font-bold tabular-nums ${strong ? "text-lg text-primary" : "text-base"} ${tone === "danger" ? "text-danger" : "text-ink"}`}>{v}</p>
  </div>
);

// Read-only Daily Closing summary — shared by the Sales History closing modal
// and the Owner Dashboard "Today's Closing Status" drill-down (view only).
export function ClosingSummaryContent({ data, date, branchId, showPdf = true }) {
  const toast = useToast();
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      await openDoc("/sales/closing-pdf", { date: date || data?.date, branch_id: branchId || data?.branch_id, lang: "id" });
    } catch { toast.error("Gagal membuat PDF"); } finally { setExporting(false); }
  };

  if (!data) return <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div>;

  const chans = data.channels || [];
  const offlineRow = chans.find((c) => c.key === "offline");
  const marketRows = chans.filter((c) => c.key !== "offline" && (c.transactions > 0 || c.bill > 0));
  const fin = data.finance;

  return (
    <div className="space-y-4" data-testid="closing-summary">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-line bg-app px-4 py-3">
        <div>
          <p className="flex items-center gap-1 text-[11px] text-ink-mute"><Hash size={11} /> No. Tutup Harian</p>
          <p className="font-heading text-sm font-bold text-ink" data-testid="closing-number">
            {data.closing_number || <span className="font-normal text-ink-mute">— (belum difinalisasi)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data.already_locked
            ? <Badge tone="info"><Lock size={11} /> Terkunci</Badge>
            : <Badge tone="warn">Draft</Badge>}
          {showPdf && (
            <Button variant="outline" size="sm" onClick={exportPdf} loading={exporting} data-testid="export-pdf-btn">
              <FileDown size={14} /> Export PDF
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          { l: "Total Transaksi", v: data.total_transactions, t: "transactions" },
          { l: "Total Item", v: data.total_items, t: "items" },
          { l: "Total Penjualan", v: rp(data.total_sales), t: "sales" },
          { l: "Draft", v: data.draft_count, t: "drafts" },
          { l: "Terkunci", v: data.finalized_count, t: "finalized" },
        ].map((s) => (
          <div key={s.l} className="rounded-xl bg-app px-3 py-2.5" data-testid={`closing-stat-${s.t}`}>
            <p className="text-[11px] text-ink-mute">{s.l}</p>
            <p className="font-heading text-lg font-bold text-ink">{s.v}</p>
          </div>
        ))}
      </div>

      {fin && (
        <div className="overflow-hidden rounded-xl border border-line" data-testid="closing-finance">
          <div className="border-b border-line bg-app px-4 py-2.5">
            <p className="flex items-center gap-1.5 text-xs font-bold text-ink-soft"><Landmark size={13} /> Ringkasan Keuangan Harian</p>
          </div>
          <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {offlineRow && (
              <div className="rounded-xl bg-app px-4 py-3 text-sm" data-testid="closing-fin-offline">
                <p className="mb-1 font-bold text-ink">Offline (Dine-in)</p>
                <FinRow l="Bill Revenue" v={rp(offlineRow.bill)} />
                <FinRow l="Service Charge" v={rp(offlineRow.service)} tone="success" />
                <FinRow l="PB1 Liability" v={rp(offlineRow.pb1)} tone="danger" />
                <FinRow l="Customer Payment" v={rp(offlineRow.customer_payment)} strong />
              </div>
            )}
            {marketRows.map((c) => (
              <div key={c.key} className="rounded-xl bg-app px-4 py-3 text-sm" data-testid={`closing-fin-${c.key}`}>
                <p className="mb-1 font-bold text-ink">{c.label}</p>
                <FinRow l="Gross Sales" v={rp(c.bill)} />
                <FinRow l="Marketplace Fee" v={`- ${rp(c.marketplace_fee)}`} tone="danger" />
                <FinRow l="Net Settlement" v={rp(c.net_settlement)} strong />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-px border-t border-line bg-line sm:grid-cols-4">
            <Tot l="Restaurant Revenue" v={rp(fin.restaurant_revenue)} testid="closing-tot-restaurant" />
            <Tot l="PB1 Liability" v={rp(fin.pb1_liability)} tone="danger" testid="closing-tot-pb1" />
            <Tot l="Marketplace Settlement" v={rp(fin.marketplace_settlement)} testid="closing-tot-marketplace" />
            <Tot l="Estimated Net Revenue" v={rp(fin.estimated_net_revenue)} strong testid="closing-tot-net" />
          </div>
        </div>
      )}

      {data.missing_recipe_menus?.length > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="closing-missing-recipe">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span><b>Peringatan Resep Belum Diset ({data.missing_recipe_menus.length}):</b> {data.missing_recipe_menus.join(", ")}. Menu ini terjual tanpa BOM sehingga stok tidak terpotong.</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-ink-soft"><Receipt size={13} /> Penjualan per Channel</p>
          <div className="overflow-hidden rounded-xl border border-line">
            <table className="w-full text-sm">
              <thead className="bg-app text-left text-xs uppercase text-ink-mute"><tr><th className="px-3 py-2">Channel</th><th className="px-3 py-2 text-center">Trx</th><th className="px-3 py-2 text-center">Item</th><th className="px-3 py-2 text-right">Rp</th></tr></thead>
              <tbody className="divide-y divide-line">
                {(data.channels || []).map((c) => (
                  <tr key={c.key} data-testid={`closing-channel-${c.key}`}>
                    <td className="px-3 py-2 font-medium text-ink">{c.label}</td>
                    <td className="px-3 py-2 text-center tabular-nums text-ink-soft">{c.transactions}</td>
                    <td className="px-3 py-2 text-center tabular-nums text-ink-soft">{c.items}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-ink">{rp(c.total_sales)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-ink-soft"><TrendingUp size={13} /> Top 10 Menu Terlaris</p>
          <div className="overflow-hidden rounded-xl border border-line">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-line">
                {(data.top_menu || []).length === 0 && <tr><td className="px-3 py-4 text-center text-sm text-ink-mute">Belum ada penjualan.</td></tr>}
                {(data.top_menu || []).map((m, i) => (
                  <tr key={m.menu_item_id} data-testid={`closing-top-${m.menu_item_id}`}>
                    <td className="px-3 py-2 text-ink"><span className="mr-2 text-ink-mute">{i + 1}.</span>{m.name}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-ink">{m.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div>
        <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-ink-soft"><Package size={13} /> Estimasi Konsumsi Inventory (Top 20)</p>
        <div className="max-h-56 overflow-y-auto rounded-xl border border-line">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-app text-left text-xs uppercase text-ink-mute"><tr><th className="px-3 py-2">Bahan</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Est. Nilai</th></tr></thead>
            <tbody className="divide-y divide-line">
              {(data.estimated_consumption || []).length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-center text-sm text-ink-mute">Tidak ada konsumsi.</td></tr>}
              {(data.estimated_consumption || []).map((r) => (
                <tr key={r.product_id} data-testid={`closing-consume-${r.product_id}`}>
                  <td className="px-3 py-2 text-ink">{r.product_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-ink-soft">{r.quantity} {r.unit || ""}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-ink">{rp(r.est_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex items-center gap-2 rounded-xl border border-line px-4 py-3 text-sm" data-testid="closing-incidents">
          <AlertTriangle size={16} className="text-amber-500" />
          <span className="text-ink-soft">Incident hari ini: <b className="text-ink">{data.incident_count}</b></span>
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-line px-4 py-3 text-sm">
          <Receipt size={16} className="text-ink-mute" />
          <span className="text-ink-soft">Struk terlampir: <b className="text-ink">{data.receipt_count}</b></span>
        </div>
      </div>

      {data.closing_notes && (
        <div>
          <p className="mb-1 text-xs font-semibold text-ink-soft">Catatan Penutupan</p>
          <p className="rounded-xl bg-app px-4 py-3 text-sm text-ink" data-testid="closing-notes-display">{data.closing_notes}</p>
        </div>
      )}
    </div>
  );
}
